import { useState, useEffect } from 'react';
import { MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FeedbackDialog } from '@/components/shared/FeedbackDialog';
import { UserFeedbackInbox } from '@/components/shared/UserFeedbackInbox';
import { UpgradeDialog } from '@/components/shared/UpgradeDialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getUserFeedback } from '@/lib/dailyUsage';
import { useAuth } from '@/contexts/AuthContext';

export function FloatingFeedbackButton() {
  const { user } = useAuth();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Poll for unread feedback replies every 10 seconds
  useEffect(() => {
    if (!user) return;

    const checkUnread = async () => {
      try {
        const feedback = await getUserFeedback(user.uid);
        const count = feedback.filter(f => f.hasUnreadReply).length;
        setUnreadCount(count);
      } catch (error) {
        console.error('Failed to check unread feedback:', error);
      }
    };

    checkUnread();
    const interval = setInterval(checkUnread, 10000);
    return () => clearInterval(interval);
  }, [user]);

  const hasUnread = unreadCount > 0;

  const handleMainButtonClick = () => {
    if (hasUnread) {
      setInboxOpen(true);
    } else {
      setFeedbackOpen(true);
    }
  };

  return (
    <>
      <div className="fixed bottom-36 right-4 sm:bottom-24 sm:right-6 z-40 flex flex-col gap-3">
        {/* Main Feedback Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={handleMainButtonClick}
              size="icon"
              className="relative h-12 w-12 sm:h-14 sm:w-14 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground"
            >
              <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6" />
              {hasUnread && (
                <span className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-red-600 text-white text-sm font-bold flex items-center justify-center shadow-lg ring-2 ring-white animate-pulse">
                  {unreadCount}
                </span>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" className="font-medium">
            {hasUnread ? `${unreadCount} New ${unreadCount === 1 ? 'Reply' : 'Replies'}!` : 'Send Feedback'}
          </TooltipContent>
        </Tooltip>
      </div>

      <FeedbackDialog
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        onUpgrade={() => {
          setFeedbackOpen(false);
          setUpgradeOpen(true);
        }}
      />

      <UserFeedbackInbox
        open={inboxOpen}
        onOpenChange={setInboxOpen}
        onUnreadCountChange={setUnreadCount}
      />

      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        reason="general"
      />
    </>
  );
}
